import { useCallback, useContext, useState } from "react";
import useAuth from "./useAuth";
import useCookie from "./useCookie";
import useCustomNavigate from "./useCustomNavigate";
import useCurrentGame from "./useCurrentGame";
import INTOUCH_API from "../services/Api";
import Game from "../store/Game";
import PersonalAccount from "../store/PersonalAccount";
import useModalActions from "./useModalActions";
import { MODAL_TYPES } from "../utils/emitter";
import ROUTES from "../utils/routes";
import is from "is_js";
import { SocketContext, SOCKET_EVENTS } from "../components/SocketProvider";
import { TEMPORARY_ID_KEY } from "../utils/constants";
import useBrowserStorage from "./useBrowserStorage";

const GENDER_ERRORS = [
  "You can't play with the same gender. Change your gender to accept the invitation",
  "Нельзя играть, одного пола. Смени пол для принятия приглашения.",
];

export default function useJoinGame() {
  const { token } = useAuth();
  const { setCookie, removeCookie } = useCookie();
  const { navigateTo } = useCustomNavigate();
  const { setGame } = useCurrentGame();
  const { getItem, removeItem } = useBrowserStorage();
  const { openModal } = useModalActions();
  const { sendEvent } = useContext(SocketContext);

  const [isLoading, setIsLoading] = useState(false);

  const joinGame = useCallback(
    async (link) => {
      const gameLink = link || "";

      // Если ни токена, ни ссылки — делать нечего
      if (!token && !gameLink) return;

      // Неавторизованный пользователь с инвайт‑ссылкой —
      // сохраняем ссылку в куку, чтобы использовать после регистрации/логина
      if (!token && gameLink) {
        const inTwentyMinutes = new Date(new Date().getTime() + 20 * 60 * 1000);
        setCookie("invite_link", gameLink, {
          sameSite: "strict",
          expires: inTwentyMinutes,
          secure: true,
        });
      }

      // Авторизованный без ссылки — выходим
      if (token && !gameLink) return;

      setIsLoading(true);

      const isIos = is.ios();

      const temporaryUserId = getItem(TEMPORARY_ID_KEY);
      await new Promise((resolve) => {
        INTOUCH_API.requestManager(
          "join_game",
          {
            payload: {
              link: gameLink,
              platform: isIos ? "iOS" : "",
              temporaryUserId,
            },
          },
          (response) => {
            if (response.data?.name) {
              PersonalAccount.getPersonalAccountData().then(async () => {
                await PersonalAccount.addGameIdsByMeets(
                  response.data.id,
                  response.data.invitedUserId
                );
                const game = PersonalAccount.games.find(
                  (g) => g.id === response.data.id
                );
                if (!game) {
                  resolve();
                  return;
                }

                removeItem(TEMPORARY_ID_KEY);

                setGame(game);
                Game.setGame(game);

                sendEvent(SOCKET_EVENTS.REQUEST_REFRESH_GAME_DATA, {
                  invitedUserId: game?.invitedUserId,
                  inviterUserId: game?.userId,
                  room: game.id,
                });

                // Поведение как раньше — всегда ведём в личный кабинет
                navigateTo(ROUTES.PERSONAL_ACCOUNT_PAGE, { replace: true });
                resolve();
              });
            } else {
              resolve();
            }
          },
          (error) => {
            // item 4: было 2 минуты — кука «пол назначен пригласившим» протухала, пока юзер
            // заполнял форму регистрации, и пол снова становился доступен для смены
            // (клиент: «пол было возможно поменять»). 20 минут — как у invite_link выше.
            const inviteCookiesExpire = new Date(
              new Date().getTime() + 20 * 60 * 1000
            );

            if (error?.response?.data?.message?.userGender) {
              const { userGender, game } = error.response.data.message;

              setCookie("inviterGender", userGender, {
                sameSite: "strict",
                expires: inviteCookiesExpire,
                secure: true,
              });
              setCookie("isInvited", 1, {
                sameSite: "strict",
                expires: inviteCookiesExpire,
                secure: true,
              });
              setCookie("gameType", game.type, {
                sameSite: "strict",
                expires: inviteCookiesExpire,
                secure: true,
              });
              setCookie("isWatchFirstTimeGameModal", 1, {
                sameSite: "strict",
                expires: inviteCookiesExpire,
                secure: true,
              });
              navigateTo(ROUTES.REGISTER_PAGE, { replace: true });
            } else {
              const message = error?.response?.data?.message;

              if (GENDER_ERRORS.includes(message)) {
                navigateTo(ROUTES.PERSONAL_ACCOUNT_PAGE, {
                  replace: true,
                  state: { event: "GENDER_NOTIFICATION", link: gameLink },
                });
                setCookie("errorLink", "isError", {
                  sameSite: "strict",
                  expires: inviteCookiesExpire,
                  secure: true,
                });
              } else {
                removeCookie("invite_link");
                openModal(MODAL_TYPES.ALREADY_USED_LINK, message);
                setCookie("errorLink", "isError", {
                  sameSite: "strict",
                  expires: inviteCookiesExpire,
                  secure: true,
                });
                navigateTo(ROUTES.LANDING_PAGE, { replace: true });
              }
            }
            resolve();
          },
          () => {
            setIsLoading(false);
          }
        );
      });
    },
    [token, setCookie, removeCookie, navigateTo, setGame, openModal, sendEvent]
  );

  return {
    joinGame,
    isLoading,
  };
}
